/** @format */

import produce from "immer";
import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { useUpdateResourceReferenceMutation } from "../../../app/services/resourcesApi";
import { useUpdateUserResourceReferenceMutation } from "../../../app/services/userResourceApi";
import { ResourceReference, UserResourceReference } from "../../../types";
import { isInGroup } from "../../auth/authSlice";

import { ReactComponent as TagIcon } from "bootstrap-icons/icons/tag.svg";
import { ReactComponent as X } from "bootstrap-icons/icons/x.svg";
import { ReactComponent as Plus } from "bootstrap-icons/icons/plus.svg";
import BLSpinner from "../../../assets/animations/BLSpinner/BLSpinner";
import "./EditUserResourceModal.scss";
import HelperTooltip from "../helperTooltip/HelperTooltip";
import {
  getQualificationKeystages,
  isSecureLinkUndefinedWrapper,
  qualIdToFull,
} from "../../../utils/functions";
import SpecAccordion from "../specAccordion/SpecAccordion";
import { getObjectDiff } from "./ObjectDiff";
import { DeepPartial } from "@reduxjs/toolkit";

type Props = {
  resourceRef: UserResourceReference | ResourceReference;
};

const EditResourceRefModal: React.FC<Props> = ({ resourceRef }) => {
  const { t: _ } = useTranslation();
  const isStaff = useAppSelector(isInGroup(["staff"]));

  const [data, setData] = useState<UserResourceReference | ResourceReference>({
    ...resourceRef,
    Status: resourceRef.Status ?? "draft",
  });

  // Keeps the modal data in sync with any changes
  useEffect(() => {
    setData({ ...resourceRef, Status: resourceRef.Status ?? "draft" });
  }, [resourceRef]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);

  const [title, setTitle] = useState<string>(data.Object.title || "");
  const [description, setDescription] = useState<string>(
    data.Object.description || "",
  );

  const [updateUserResourceReference, { isLoading: isUserResourceLoading }] =
    useUpdateUserResourceReferenceMutation();
  const [updateResourceReference, { isLoading: isResourceLoading }] =
    useUpdateResourceReferenceMutation();

  const isLoading: boolean = isResourceLoading || isUserResourceLoading;

  const isInvalidThumbnail: boolean = !Boolean(
    !data.Object.thumbnail || data.Object.thumbnail.startsWith("https://"),
  );

  const handleClose = () => {
    setData(resourceRef);
    setShowModal(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === true) {
      if (resourceRef.PK.includes("USER:")) {
        // TODO: when user resources are implemented, this will need to send only the changes, not the whole object
        await updateUserResourceReference({
          ...data,
        } as any);
      } else if (isStaff && !resourceRef.PK.includes("USER:")) {
        // NOTE: any is used here on purpose not being lazy
        const refDiff: DeepPartial<ResourceReference> = getObjectDiff(
          resourceRef as any,
          data as any,
        );

        if (Object.keys(refDiff).length !== 0) {
          await updateResourceReference({
            primaryKey: { PK: resourceRef.PK, SK: resourceRef.SK },
            refInformation: {
              locale: resourceRef.Locale,
              type: resourceRef.Type,
              code: resourceRef.Code,
            },
            changes: refDiff,
          });
        } // Only send request if changes are found
      }
      handleClose();
    }

    setValidated(true);
  };

  const handleObjectChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let name: string = e.target.name;
    let val: string = e.target.value;

    setData({ ...data, Object: { ...data.Object, [name]: val } });
  };

  const handleItemChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    let name: string = e.target.name;
    let val: string = e.target.value;

    setData({ ...data, [name]: val });
  };

  const handleLevelChange = (selectedLevel: string, checked: boolean) => {
    let levels = data.Object.levels;

    if (!checked && levels.includes(selectedLevel)) {
      setData({
        ...data,
        Object: {
          ...data.Object,
          levels: [
            ...levels.slice(0, levels.indexOf(selectedLevel)),
            ...levels.slice(levels.indexOf(selectedLevel) + 1),
          ],
        },
      });
    } else if (checked && !levels.includes(selectedLevel)) {
      setData({
        ...data,
        Object: { ...data.Object, levels: [...levels, selectedLevel] },
      });
    }
  };

  const handleRadioChange = (eTarget: Element) => {
    setData({
      ...data,
      Object: {
        ...data.Object,
        fundedBy:
          eTarget.id === "N/A" ||
          eTarget.id === "Adnodd" ||
          eTarget.id === "WelshGov"
            ? eTarget.id
            : "N/A",
      },
    });
  };

  const handleUpdateAuthors = (value: string) => {
    if (isStaff)
      setData(
        produce(data, (draft) => {
          draft.Object.authors = value;
        }),
      );
  };

  const handleUpdateTag = (value?: string, index?: number) => {
    if (isStaff) {
      // EDIT
      if (index !== undefined) {
        setData(
          produce(data, (draft) => {
            draft.Object.tags[index] = value ?? "";
          }),
        );
      }
      // ADD
      else {
        setData(
          produce(data, (draft) => {
            if (!draft.Object.tags) {
              draft.Object.tags = [];
            }
            draft.Object.tags.push("");
          }),
        );
      }
    }
  };

  const handleRemoveTag = (index: number) => {
    if (isStaff)
      setData(
        produce(data, (draft) => {
          draft.Object.tags.splice(index, 1);
        }),
      );
  };

  return (
    <>
      <Button
        aria-label={`edit ${
          resourceRef.PK.includes("USER:") ? "user-resource" : "resource"
        } details`}
        variant="editing"
        className="px-3 w-100"
        onClick={() => {
          setValidated(false);
          setShowModal(true);
        }}
      >
        {_("Edit resource details")}
      </Button>
      <Modal
        show={showModal}
        onHide={() => handleClose()}
        className="resource-ref-modal"
        aria-label="edit-resource-ref-modal"
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          {resourceRef.PK.includes("USER:")
            ? _("User resource details")
            : _("Resource details")}
        </Modal.Header>
        <Form
          role={"form"}
          onSubmit={handleSubmit}
          validated={validated}
          noValidate
        >
          <Modal.Body>
            <Form.Group>
              <h5>
                <span style={{ color: "red" }}>*</span> {_("Title")}
              </h5>
              <Form.Control
                name="title"
                aria-describedby="Resource title input"
                className="mb-4"
                value={data.Object.title || ""}
                onChange={(e) => {
                  setTitle(e.currentTarget.value);
                  handleObjectChange(e as React.ChangeEvent<HTMLInputElement>);
                }}
                aria-label="Resource title input"
                isInvalid={!title}
              />
            </Form.Group>

            <Form.Group>
              <h5>
                <span style={{ color: "red" }}>*</span> {_("Description")}
              </h5>
              <Form.Control
                name="description"
                aria-describedby="Resource description input"
                className="mb-4"
                value={data.Object.description || ""}
                onChange={(e) => {
                  setDescription(e.currentTarget.value);
                  handleObjectChange(e as React.ChangeEvent<HTMLInputElement>);
                }}
                as="textarea"
                rows={3}
                isInvalid={!description}
              />
            </Form.Group>

            {isStaff && (
              <>
                <Form.Group className="mb-3">
                  <h5>
                    {_("Image")}
                    <HelperTooltip type="conditionalSync" tooltipText={_("conditionalSyncInformation")} />
                  </h5>
                  <Form.Label>
                    {_("Paste the Cloudberry link below:")}
                  </Form.Label>
                  <Form.Control
                    name="thumbnail"
                    aria-describedby="Resource image"
                    isInvalid={
                      !isSecureLinkUndefinedWrapper(data.Object.thumbnail || "")
                    }
                    onChange={(e) =>
                      handleObjectChange(
                        e as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                    value={data.Object.thumbnail ?? ""}
                  />
                  <Form.Control.Feedback type="invalid">
                    {_("Please enter a secure link (https)")}
                  </Form.Control.Feedback>
                </Form.Group>
                {data.Object.thumbnail && !isInvalidThumbnail && (
                  <div
                    className="mb-3 rounded position-relative"
                    style={{
                      backgroundImage: `url('${data.Object.thumbnail}')`,
                      backgroundPosition: "center",
                      paddingTop: "35%",
                      backgroundSize: "cover",
                    }}
                  ></div>
                )}

                <Form.Group className="mb-4">
                  <h5>
                    {_("Acknowledgements")}
                    <HelperTooltip type="conditionalSync" tooltipText={_("conditionalSyncInformation")} />
                    <HelperTooltip
                      tooltipText={_("acknowledgementsInformation")}
                    />
                  </h5>
                  <Form.Label>
                    {_("Paste the Cloudberry link below:")}
                  </Form.Label>
                  <Form.Control
                    name="acknowledgements"
                    aria-describedby="Resource acknowledgements"
                    value={data.Object.acknowledgements?.toLocaleString() || ""}
                    isInvalid={
                      !isSecureLinkUndefinedWrapper(
                        data.Object.acknowledgements?.toLocaleString() || "",
                      )
                    }
                    onChange={(e) =>
                      handleObjectChange(
                        e as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {_("Please enter a secure link (https)")}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                  <h5>
                    {_("Branding")}
                    <HelperTooltip type="sync" tooltipText={_("syncInformation")} />
                  </h5>
                  <Form.Select
                    name="Branding"
                    className="mb-4"
                    value={data.Branding}
                    onChange={(e) => handleItemChange(e)}
                  >
                    <option value="" disabled>
                      {_("Select a brand")}
                    </option>
                    <option value="cbac&wjec">WJEC & CBAC</option>
                    <option value="cbac">CBAC</option>
                    <option value="wjec">WJEC</option>
                    <option value="eduqas">Eduqas</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group
                  className="mb-4"
                  onChange={(e) => {
                    handleRadioChange(e.target as Element);
                  }}
                >
                  <h5>
                    {_("Funded By")}
                    <HelperTooltip type="sync" tooltipText={_("syncInformation")} />
                    <HelperTooltip tooltipText={_("fundedByInformation")} />
                  </h5>

                  <Form.Check
                    defaultChecked={data.Object.fundedBy === "N/A"}
                    inline
                    label={_("N/A")}
                    type="radio"
                    name="fundedBy"
                    id="N/A"
                  />

                  <Form.Check
                    defaultChecked={data.Object.fundedBy === "Adnodd"}
                    inline
                    label="Adnodd"
                    type="radio"
                    name="fundedBy"
                    id="Adnodd"
                  />

                  <Form.Check
                    defaultChecked={data.Object.fundedBy === "WelshGov"}
                    inline
                    label={_("Welsh Government")}
                    type="radio"
                    name="fundedBy"
                    id="WelshGov"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <h5>{_("Qualification")}</h5>
                  <Form.Control
                    value={qualIdToFull(data.Qualification)}
                    disabled={true}
                  />
                </Form.Group>

                <Form.Group
                  className="mb-4"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleLevelChange(e.target.id, e.target.checked)
                  }
                >
                  <h5>
                    {_("Key Stages")}
                    <HelperTooltip type="sync" tooltipText={_("syncInformation")} />
                    <HelperTooltip tooltipText={_("keystageInformation")} />
                  </h5>
                  {["KS2", "KS3", "KS4", "KS5"].map(
                    (keystage: string, index: number) => (
                      <Form.Check
                        key={`${keystage}-${index}`}
                        label={`${keystage}`}
                        name="levels"
                        type="checkbox"
                        id={keystage}
                        // Assumes that the incoming data contains the correct default keystage
                        defaultChecked={data.Object.levels?.includes(keystage)}
                        disabled={
                          data.Qualification
                            ? getQualificationKeystages(
                                data.Qualification,
                              ).includes(keystage)
                            : false
                        }
                      />
                    ),
                  )}
                </Form.Group>

                <Form.Group>
                  <h5>
                    {_("Spec item")}
                    <HelperTooltip type="sync" tooltipText={_("syncInformation")} />
                    <HelperTooltip tooltipText={_("specItemInformation")} />
                  </h5>
                  <SpecAccordion
                    handleSelectedChange={(e) => handleItemChange(e)}
                    SubjectId={data.Subject.id}
                    Qualification={data.Qualification}
                    DefaultSelected={data.SpecItem}
                  />
                </Form.Group>

                <Form.Group>
                  <h5>
                    {_("Author")}
                    <HelperTooltip type="conditionalSync" tooltipText={_("conditionalSyncInformation")} />
                    <HelperTooltip tooltipText={_("authorInformation")} />
                  </h5>
                  <Form.Control
                    name="author"
                    aria-describedby="Resource author input"
                    className="mb-4"
                    value={data.Object.authors || ""}
                    onChange={(e) => handleUpdateAuthors(e.currentTarget.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <h5>{_("Synopsis")}</h5>
                  <Form.Control
                    name="synopsis"
                    aria-describedby="Resource description input"
                    className="mb-4"
                    value={data.Object.synopsis}
                    onChange={(e) =>
                      handleObjectChange(
                        e as React.ChangeEvent<HTMLInputElement>,
                      )
                    }
                    as="textarea"
                    rows={3}
                  />
                </Form.Group>

                <Form.Group>
                  <h5>{_("Tags")}</h5>
                  <div className="tags mb-4 d-flex gap-2 flex-wrap">
                    {data.Object.tags &&
                      data.Object.tags.map((tag, tagIndex) => (
                        <div
                          className="tag-container d-flex"
                          id={`tag-${tagIndex + 1}`}
                          key={`${tagIndex + 1}`}
                        >
                          <InputGroup>
                            <InputGroup.Text className="tag">
                              <TagIcon />
                            </InputGroup.Text>
                            <Form.Control
                              value={tag || ""}
                              onChange={(e: any) => {
                                handleUpdateTag(
                                  e.currentTarget.value,
                                  tagIndex,
                                );
                              }}
                              aria-label="Tag input"
                            />
                            <Button
                              className="delete-tag"
                              variant="light"
                              onClick={() => {
                                handleRemoveTag(tagIndex);
                              }}
                              aria-label="Delete tag button"
                            >
                              <X className="tag-delete-icon" />
                            </Button>
                          </InputGroup>
                        </div>
                      ))}
                    <Button
                      variant="outline-editing"
                      size="sm"
                      onClick={() => handleUpdateTag()}
                      aria-label="Add tag button"
                    >
                      <Plus />
                    </Button>
                  </div>
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-4">
              <h5>{_("Status")}</h5>
              <Form.Select
                name="Status"
                onChange={(e) => handleItemChange(e)}
                value={data.Status}
              >
                <option value={"draft"}>{_("draft")}</option>
                <option value={"live"}>{_("live")}</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <div className="buttons-container  justify-content-end">
              <div className="form-action-buttons d-flex gap-2">
                <Button
                  variant="outline-editing"
                  disabled={isLoading}
                  onClick={() => handleClose()}
                >
                  {_("Cancel")}
                </Button>
                {
                  <Button
                    aria-label="save"
                    className="outline-editing"
                    variant={`${isLoading ? "outline-editing" : "editing"}`}
                    disabled={isLoading || !title || !description}
                    type="submit"
                  >
                    {isLoading ? <BLSpinner /> : _("Save")}
                  </Button>
                }
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default EditResourceRefModal;
